import React, { useEffect, useState, useMemo, useContext, useRef } from "react";
import SmuButton from "../../utils/smu-button/SmuButton";
import { Card, Form } from "react-bootstrap";
import { IconDownload } from "../../icons/Icon";
import SmuBadge from "../../utils/smu-badge/SmuBadge";
import SmuSearch from "../../utils/smu-search/SmuSearch";
import { downloadFaldonXlsx } from "../../api/files";
import { toast } from "../../helpers/smu-alerts/smu-lerts";
import iconPdf from "../../assets/images/dummy/icon-pdf.png";
import { prepareText, resolveUserTienda } from "../../helpers";
import SmuTable from "../../utils/smu-table/SmuTable";
import { StoreContext } from "../../store/StoreContext";
import { IconEye, IconSearch } from "../../icons/Icon";
import { fetchFaldonesByCategoryAndTienda, getLatestDownloadFaldonStatus } from "../../api/files";

// Date validation utility function
const validateDate = (dateString) => {
  if (!dateString) return { isValid: true, error: null };
  
  // Check if the date string matches YYYY-MM-DD format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) {
    return { isValid: false, error: "Formato de fecha inválido. Use DD-MM-AAAA" };
  }
  
  // Parse the date
  const date = new Date(dateString);
  
  // Check if it's a valid date
  if (isNaN(date.getTime())) {
    return { isValid: false, error: "Fecha inválida" };
  }
  
  // Check if date is more than 100 years in the future
  const currentYear = new Date().getFullYear();
  const dateYear = date.getFullYear();
  if (dateYear > currentYear + 100) {
    return { isValid: false, error: "La fecha no puede ser más de 100 años en el futuro" };
  }
  
  // Check if date is more than 100 years in the past
  if (dateYear < currentYear - 100) {
    return { isValid: false, error: "La fecha no puede ser más de 100 años en el pasado" };
  }
  
  return { isValid: true, error: null };
};

const SkirtTable = ({ title, files, setFiles, loading, clickItem, allStores  }) => {
  const [search, setSearch] = useState("");
  const [handleFiles, setHandleFiles] = useState([]);
  const [dowloading, setDowloading] = useState(null);
  const { skirts, isAdmin, loggedUser } = useContext(StoreContext);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [rowCopies, setRowCopies] = useState({});
  const [tiendaFilter, setTiendaFilter] = useState(isAdmin ? null : resolveUserTienda(isAdmin, loggedUser));

  // Filter states
  const [tipoCampana, setTipoCampana] = useState("");
  const [seccion, setSeccion] = useState("");
  const [ean, setEan] = useState("");
  const [nombreCampana, setNombreCampana] = useState("");
  const [producto, setProducto] = useState("");
  const [marca, setMarca] = useState("");
  const [categoria, setCategoria] = useState("");
  const [codigoSeccion, setCodigoSeccion] = useState("");
  const [filters, setFilters] = useState({campana: "", producto: "", marca: "", categoria:"", ean:"", created_at_from: "", created_at_to: "", start_date_from: "", start_date_to: "", end_date_from: "", end_date_to: "", tienda: ""});
  // Date filter states
  const [createdAtFrom, setCreatedAtFrom] = useState("");
  const [createdAtTo, setCreatedAtTo] = useState("");
  const [startDateFrom, setStartDateFrom] = useState("");
  const [startDateTo, setStartDateTo] = useState("");
  const [endDateFrom, setEndDateFrom] = useState("");
  const [endDateTo, setEndDateTo] = useState("");
  const adminSelectRef = useRef(null);
  const [adminTypeahead, setAdminTypeahead] = useState("");
  const adminTypeaheadTimerRef = useRef(null);
  const [adminStoreSearch, setAdminStoreSearch] = useState("");
  const [isAdminStoreDropdownOpen, setIsAdminStoreDropdownOpen] = useState(false);
  const adminDropdownRef = useRef(null);
  const filteredStores = useMemo(() => {
    const list = Array.isArray(allStores) ? allStores : [];
    const query = (adminStoreSearch || "").trim().toLowerCase();
    if (!query) return list;
    return list.filter((store) => {
      const code = String(store?.codLocal ?? store?.codigoLocal ?? store?.codigo ?? store?.id ?? "");
      const label = /^\d+$/.test(code) ? parseInt(code, 10).toString().padStart(4, '0') : code;
      const desc = String(store?.descripcion ?? store?.description ?? "").toLowerCase();
      return label.toLowerCase().includes(query) || desc.includes(query);
    });
  }, [allStores, adminStoreSearch]);

  const getAdminSelectedStoreDisplay = () => {
    if (!tiendaFilter) return 'Todas';
    const code = String(tiendaFilter);
    return /^\d+$/.test(code) ? parseInt(code, 10).toString().padStart(4, '0') : code;
  };
  
  // Date validation error states
  const [createdAtFromError, setCreatedAtFromError] = useState("");
  const [createdAtToError, setCreatedAtToError] = useState("");
  const [startDateFromError, setStartDateFromError] = useState("");
  const [startDateToError, setStartDateToError] = useState("");
  const [endDateFromError, setEndDateFromError] = useState("");
  const [endDateToError, setEndDateToError] = useState("");
  
  // Toggle state for date filters visibility
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const _skirts = skirts.map((skirt) => ({
      ...skirt,
      parent: skirt?.parent || 0,
    }));
    setTreeData(_skirts);
  }, [skirts]);

  // Tipos de campaña
  // Son todos los nodos del primer nivel del arbol skirts
  const tiposCampana = treeData.filter((skirt) => skirt.parent === 0).map((skirt) => ({
    value: skirt.id,
    label: skirt.title,
  }));

  useEffect(() => {
    const _secciones = tipoCampana ? treeData.filter((skirt) => parseInt(skirt.parent) == parseInt(tipoCampana)).map((skirt) => ({
      value: skirt.id,
      label: skirt.title,
    })) : [];
    setSecciones(_secciones);
    setSeccion("");
  }, [tipoCampana]);

  const handleAdminSelectKeyDown = (e) => {
    if (!isAdmin) return;
    const key = e.key;
    // Accept letters, numbers, and comma
    if (key.length === 1 && /[a-zA-Z0-9,]/.test(key)) {
      e.preventDefault();
      const next = (adminTypeahead + key).toLowerCase();
      setAdminTypeahead(next);
      if (adminTypeaheadTimerRef.current) clearTimeout(adminTypeaheadTimerRef.current);
      adminTypeaheadTimerRef.current = setTimeout(() => setAdminTypeahead(""), 700);
      const list = Array.isArray(allStores) ? allStores : [];
      const match = list.find((store) => {
        const code = String(store?.codLocal ?? store?.codigoLocal ?? store?.codigo ?? store?.id ?? "");
        return code.toLowerCase().includes(next);
      });
      if (match) {
        const code = String(match?.codLocal ?? match?.codigoLocal ?? match?.codigo ?? match?.id ?? "");
        setTiendaFilter(code);
      }
    } else if (key === "Backspace") {
      e.preventDefault();
      const next = adminTypeahead.slice(0, -1);
      setAdminTypeahead(next);
    }
  };

  useEffect(() => {
    let _files = files;
    if (!!search) {
      const _search = prepareText(search.trim());
      _files = files.filter((_file) => {
        const filename = prepareText(_file.filename);
        return filename.includes(_search);
      });
    }

    setHandleFiles(_files);
  }, [files, search]);

  // Date validation handlers
  const handleDateChange = (setter, errorSetter, value) => {
    setter(value);
    const validation = validateDate(value);
    errorSetter(validation.error || "");
  };

  const download = async (file, copies = 1) => {
    const ean = file.DOWNLOADED ? filters.ean : "";
    const username = loggedUser?.username;
    const tienda = isAdmin ? null : tiendaFilter;
    setDowloading(file.id);
    setIsDownloading(true);
    setLoadingPercent(0);
  
    const statusToMax = {FILTERING: 40, RPA_VIRAL: 94, SUCCESS: 100, FAILURE: 100};
    const statusBase = { FILTERING: 1, RPA_VIRAL: 41, SUCCESS: 95, FAILURE: 95 };

    // Progress increment
    let progress = 0;
    let maxDuringDownload = statusToMax.FILTERING;
    const intervalDelay = 150;
    let randomDelayGivenStatus = 150;
    let progressInterval;
    const runProgressCycle = () => {
      randomDelayGivenStatus = progress < statusBase.RPA_VIRAL
        ? Math.floor(Math.random() * 350)
        : Math.floor(Math.random() * 350);
      const intervalStep = Math.floor(Math.random() * (progress < statusBase.RPA_VIRAL ? 3 : 3));
      progress = Math.min(progress + intervalStep, maxDuringDownload);
      setLoadingPercent(progress);
      progressInterval = setTimeout(runProgressCycle, intervalDelay + randomDelayGivenStatus);
    };
    runProgressCycle();
  
    // Polling download status every 7s
    const pollingInterval = setInterval(async () => {
      try {
        if(progress>statusToMax.FILTERING){return}
        const downloadId = file.id
          + (file.FORMATO_IMPRESION ? ("::" + file.FORMATO_IMPRESION.toLowerCase()) : "") 
          + (file.NEGOCIO ? ("::" + file.NEGOCIO.toUpperCase()) : "") 
          + (file.NOMBRE_CAMPANA ? ("::" + file.NOMBRE_CAMPANA.toLowerCase()) : "");
        const statusResp = await getLatestDownloadFaldonStatus(downloadId, username);
        const newMax = statusToMax[statusResp.REQUEST_STATUS] ?? maxDuringDownload;
        if (newMax > maxDuringDownload) {
          maxDuringDownload = newMax;
          progress = statusBase[statusResp.REQUEST_STATUS];
          setLoadingPercent(progress);
        }
      } catch (err) {
        console.warn("status fetch failed.");
        //if (progress > 44) {setTimeout(() => {maxDuringDownload = statusToMax.RPA_VIRAL; progress = statusBase.RPA_VIRAL}, 100);} //MOCKUP TEST
      }
    }, 7000);
  
    try {
      const response = await downloadFaldonXlsx(file.id, {tienda, username: username, eans: ean, copies });

      clearInterval(progressInterval);
      clearInterval(pollingInterval);
      setLoadingPercent(97); // response arrived

      if (response.status === 204) {
        toast({ icon: "info", title: "No hay eans que coincidan para el faldón" });
        setLoadingPercent(100);
        setDowloading(null);
        setIsDownloading(false);
        return;
      }

      // Extract filename from Content-Disposition header
      let downloadFilename = file.id.replace(/\.xlsx$/i, '.pdf'); // fallback
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      if (disposition) {
        const match = disposition.match(/filename=\"?([^\";]+)\"?/);
        if (match && match[1]) {
          downloadFilename = match[1];
        }
      }

      // Create a blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setLoadingPercent(99);

    } catch (err) {
      clearInterval(progressInterval);
      clearInterval(pollingInterval);
      toast({ icon: "error", title: `Error descargando ${file.filename}` });
    }

    setLoadingPercent(100);
    setDowloading(null);
    setIsDownloading(false);

    setFiles((files) =>
      files.map((f) =>
        f.filename === file.filename ? { ...f, DOWNLOADED: true, views: (f.views || 0) + 1 } : f
      )
    );
  };

  const handleCopiesChange = React.useCallback((rowId, rawValue) => {
    if (rawValue === "") {
      setRowCopies(prev => ({ ...prev, [rowId]: "" }));
      return;
    }
    const parsed = parseInt(rawValue, 10);
    if (!Number.isNaN(parsed) && parsed > 0) {
      const valueToSet = parsed > 10 ? 10 : parsed;
      setRowCopies(prev => ({ ...prev, [rowId]: valueToSet }));
    }
  }, []);

  const handleCopiesBlur = React.useCallback((rowId) => {
    setRowCopies(prev => {
      const current = prev[rowId];
      if (current === "" || current === undefined || current <= 0) {
        return { ...prev, [rowId]: 1 };
      }
      return prev;
    });
  }, []);

  const handleFilter = () => {
    // Check for validation errors before proceeding
    const hasValidationErrors = createdAtFromError || createdAtToError || 
                               startDateFromError || startDateToError || 
                               endDateFromError || endDateToError;
    
    if (hasValidationErrors) {
      toast({ icon: "error", title: "Por favor corrija los errores de validación en las fechas antes de filtrar" });
      return;
    }
    
    setLoadingData(true);
    // unused - filtering by sub-menu tree NOTE: keep logic in case of rollback
    var nodeId = null;
    if (!tipoCampana) {
      nodeId = null;
    } else {
      if (!seccion){
        nodeId = treeData.filter((skirt) => parseInt(skirt.parent) == parseInt(tipoCampana)).map((skirt) => parseInt(skirt.id).toString());
        nodeId.push(parseInt(tipoCampana).toString());
        nodeId = nodeId.join(',');
      } else {
        nodeId = parseInt(seccion).toString() || parseInt(tipoCampana).toString();
      }
    }
    if (tipoCampana == "" || tipoCampana == null) {
      nodeId = null;
    }

    setFilters({
      campana: nombreCampana || "",
      producto: producto || "",
      marca: marca || "",
      categoria: categoria || "",
      ean: ean || "",
      created_at_from: createdAtFrom || "",
      created_at_to: createdAtTo || "",
      start_date_from: startDateFrom || "",
      start_date_to: startDateTo || "",
      end_date_from: endDateFrom || "",
      end_date_to: endDateTo || "",
      tienda: tiendaFilter || ""
    });

    // Common filters
    const ean_filter = ean ? ean : null;
    const campana_filter = nombreCampana ? nombreCampana : null;
    const producto_filter = producto ? producto : null;
    const marca_filter = marca ? marca : null;
    const categoria_filter = categoria ? categoria : null;
    const tienda_filter = tiendaFilter ? tiendaFilter : null;
    
    // Date filters
    const created_at_from_filter = createdAtFrom ? createdAtFrom : null;
    const created_at_to_filter = createdAtTo ? createdAtTo : null;
    const start_date_from_filter = startDateFrom ? startDateFrom : null;
    const start_date_to_filter = startDateTo ? startDateTo : null;
    const end_date_from_filter = endDateFrom ? endDateFrom : null;
    const end_date_to_filter = endDateTo ? endDateTo : null;

    const username_filter = loggedUser && loggedUser.username ? loggedUser.username : null;
    const response = fetchFaldonesByCategoryAndTienda(
      nodeId, tienda_filter, username_filter, 
      ean_filter, codigoSeccion, campana_filter, producto_filter, marca_filter, categoria_filter,
      created_at_from_filter, created_at_to_filter, start_date_from_filter, start_date_to_filter, end_date_from_filter, end_date_to_filter
    ).then(({ data, error }) => {
      if (!!error) throw new Error(error?.message);
      const faldones = (data || []).map(faldon => ({
        ...faldon,
        filename: faldon.FALDON_XLSX.replace(/\.(xlsx|xls)$/i, ''),
        id: faldon.FALDON_XLSX,
        DOWNLOADED: faldon.DOWNLOADED,
        views: faldon.VIEWS,
      }));
      setFiles(faldones);
      setLoadingData(false);
    })
    .catch((err)=> {
      setLoadingData(false);
      toast({ icon: "error", title: "Error al traer faldones. Reintente otra vez" });
    });
  };

  const columns = useMemo(
    () => [
      /*{
        name: "",
        selector: (row) => row.id,
        sortable: true,
        cell: (row) => (
          <div className="cell text-truncate">
          </div>
        ),
        width: "0px",
      },*/
      {
        name: "Nombre archivo",
        selector: (row) => row.filename,
        sortable: true,
        cell: (row) => <div className="cell text-truncate text-wrap">{row.filename || "N/A"}</div>,
      },
      // No views/visualizado in new faldon model yet
      /*{
        name: "Visualizado",
        selector: (row) => row.views,
        sortable: true,
        cell: (row) => (
          <div className="cell text-truncate">
            {Number(row.views) > 0 ? "Si" : "No"}
          </div>
        ),
      },*/
      {
        name: "F.Publicación",
        selector: (row) => row.FECHA_HORA_CARGA,
        sortable: true,
        cell: (row) => <div className="cell text-truncate">{ row.FECHA_HORA_CARGA.split('T')[0] }</div>,
        width: "117px",
      },
      {
        name: "F.Inicio",
        selector: (row) => row.FECHA_VIGENCIA_DESDE,
        sortable: true,
        cell: (row) => <div className="cell text-truncate">{ row.FECHA_VIGENCIA_DESDE.split('T')[0] }</div>,
        width: "117px",
      },
      {
        name: "F.Fin",
        selector: (row) => row.FECHA_VIGENCIA_HASTA,
        sortable: true,
        cell: (row) => <div className="cell text-truncate">{ row.FECHA_VIGENCIA_HASTA.split('T')[0] }</div>,
        width: "117px",
      },
      {
        name: "Ver artículos",
        selector: (row) => row.id,
        sortable: false,
        cell: (row) => (
          <div className="cell text-truncate" title={row.DOWNLOADED == false ? "Debe descargar el faldon completo ver sus artículos." : ""}>
            <SmuButton
              _className="mx-auto"
              control={() => clickItem(row, filters.ean !== "" ? {ean: filters.ean, tienda: tiendaFilter} : {tienda: tiendaFilter})}
              disabled={row.DOWNLOADED == false}
            >
              <IconEye color="#33383F" />
            </SmuButton>
          </div>
        ),
        width: "100px",
      },
      {
        name: "Visualizaciones",
        selector: (row) => row.views,
        sortable: true,
        cell: (row) => <div className="cell text-truncate">{row.views}</div>,
        width: "129px",
      },
      {
        name: "Descargar",
        selector: (row) => row.id,
        sortable: true,
        cell: (row) => (
          <div className="cell text-truncate" style={{ marginRight: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* <input
              type="number"
              min={1}
              max={10}
              step={1}
              value={rowCopies[row.id] !== undefined ? rowCopies[row.id] : 1}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onChange={(e) => handleCopiesChange(row.id, e.target.value)}
              onBlur={() => handleCopiesBlur(row.id)}
              style={{ width: 60, margin: 10 }}
              title="Cantidad de copias"
            /> */}
            <SmuButton
              _className="mx-auto"
              control={() => {
                const raw = rowCopies[row.id];
                const parsed = parseInt(raw, 10);
                const copies = !isNaN(parsed) && parsed > 0 ? parsed : 1;
                download(row, copies);
              }}
              loading={dowloading === row.id}
              disabled={isDownloading}
              loadingPercent={loadingPercent}
              loadingPercentPosition={"top"}
              highlightPin={row.DOWNLOADED === false}
            >
              <IconDownload color="#33383F" />
            </SmuButton>
          </div>
        ),
        width: "110px",
      },
    ],
    [dowloading, files, isDownloading, loadingPercent, rowCopies]
  );

  return (
    <Card className="smu-card">
      <Card.Header className="d-flex flex-column py-4">
        <div className="d-flex align-items-center mb-3">
          <SmuBadge color="#FFD88D" />
          {title ? <span className="fs-5 ms-3 me-auto">Faldones | {title}</span> : <span className="fs-5 ms-3 me-auto">Faldones</span>}
        </div>
        
        {/* Filter Menu */}
        {
          !title ? (
            <div className="row g-3 align-items-end">
            
            {/*
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Tipo de campaña</Form.Label>
                  <Form.Select
                    value={tipoCampana}
                    onChange={(e) => setTipoCampana(e.target.value)}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                  >
                    <option value="">Seleccionar tipo</option>
                    {tiposCampana.map((tipo) => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Sub-menú</Form.Label>
                  <Form.Select
                    value={seccion}
                    onChange={(e) => setSeccion(e.target.value)}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                  >
                    <option value="">Seleccionar sub-menú</option>
                    {secciones.map((sec) => (
                      <option key={sec.value} value={sec.value}>
                        {sec.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              */}

              <div className="col-md-2">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Nombre campaña</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ingrese nombre de campaña"
                    value={nombreCampana}
                    maxLength={101}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 100);
                      if (e.target.value !== value) {
                        toast({
                          icon: "warning",
                          title: "Largo máximo 100 para nombre de campaña."
                        });
                      }
                      setNombreCampana(e.target.value);
                    }}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                  />
                </Form.Group>
              </div>

              <div className="col-md-2">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Producto</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ingrese producto"
                    value={producto}
                    maxLength={101}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 100);
                      if (e.target.value !== value) {
                        toast({
                          icon: "warning",
                          title: "Largo máximo 100 para producto."
                        });
                      }
                      setProducto(e.target.value);
                    }}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                  />
                </Form.Group>
              </div>

              <div className="col-md-2">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Marca</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ingrese marca"
                    value={marca}
                    maxLength={101}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 100);
                      if (e.target.value !== value) {
                        toast({
                          icon: "warning",
                          title: "Largo máximo 100 para marca."
                        });
                      }
                      setMarca(e.target.value)
                    }}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                  />
                </Form.Group>
              </div>

              <div className="col-md-2">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Categoría</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ingrese categoría"
                    value={categoria}
                    maxLength={101}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 100);
                      if (e.target.value !== value) {
                        toast({
                          icon: "warning",
                          title: "Largo máximo 100 para categoría."
                        });
                      }
                      setCategoria(e.target.value);
                    }}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                  />
                </Form.Group>
              </div>
              
              <div className="col-md-2">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">EAN 13</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Ingrese EAN"
                    value={ean}
                    maxLength={101}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9,]/g, '').slice(0, 100);
                      if (e.target.value !== value) {
                        toast({
                          icon: "warning",
                          title: "Solo números, largo máximo 100 para EAN 13."
                        });
                      }
                      setEan(value);
                    }}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                  />
                </Form.Group>
              </div>

              {loggedUser && loggedUser.store && loggedUser.store.length > 1 && !isAdmin && <div className="col-md-1">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Tienda</Form.Label>
                  <Form.Select
                    value={tiendaFilter}
                    onChange={(e) => setTiendaFilter(e.target.value)}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                  >
                    <option value="">Seleccionar tienda</option>
                    {loggedUser && loggedUser.store && !isAdmin ? loggedUser.store.map((store) => (
                      <option key={store} value={store}>{parseInt(store).toString().padStart(4, '0')}</option>
                    )) : <option value="">No hay tiendas disponibles</option>}
                  </Form.Select>
                </Form.Group>
              </div>}

              {
                isAdmin && (
                  <div className="col-md-1">
                    <Form.Group>
                      <Form.Label className="text-muted small mb-1">Tienda</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type="text"
                          className="text-dark"
                          placeholder={getAdminSelectedStoreDisplay()}
                          value={isAdminStoreDropdownOpen ? adminStoreSearch : getAdminSelectedStoreDisplay()}
                          onChange={(e) => {
                            setAdminStoreSearch(e.target.value);
                            setIsAdminStoreDropdownOpen(true);
                          }}
                          onFocus={() => setIsAdminStoreDropdownOpen(true)}
                          onBlur={(e) => {
                            if (adminDropdownRef.current && adminDropdownRef.current.contains(e.relatedTarget)) {
                              return;
                            }
                            setTimeout(() => setIsAdminStoreDropdownOpen(false), 200);
                          }}
                          style={{
                            backgroundColor: '#f4f4f4',
                            borderRadius: '12px',
                            border: 'none',
                            height: '44px'
                          }}
                          autoComplete="off"
                          ref={adminSelectRef}
                        />
                        {isAdminStoreDropdownOpen && (
                          <div
                            ref={adminDropdownRef}
                            className="position-absolute w-100 bg-white border border-secondary rounded mt-1"
                            style={{ zIndex: 1000, maxHeight: '240px', overflowY: 'auto' }}
                            onMouseDown={(e) => {
                              // prevent closing when clicking on scrollbar
                              const rect = e.currentTarget.getBoundingClientRect();
                              const scrollbarWidth = 17;
                              const isClickingOnScrollbar = e.clientX > rect.right - scrollbarWidth;
                              if (isClickingOnScrollbar) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                          >
                            <div
                              className="px-3 py-2 cursor-pointer border-bottom"
                              style={{ cursor: 'pointer' }}
                              onMouseDown={() => {
                                setTiendaFilter("");
                                setAdminStoreSearch('');
                                setIsAdminStoreDropdownOpen(false);
                              }}
                            >
                              Todas
                            </div>
                            {(!loading && Array.isArray(filteredStores) ? filteredStores : []).map((store) => {
                              const code = String(store?.codLocal ?? store?.codigoLocal ?? store?.codigo ?? store?.id ?? "");
                              const label = /^\d+$/.test(code) ? parseInt(code, 10).toString().padStart(4, '0') : code;
                              return (
                                <div
                                  key={code}
                                  className="px-3 py-2 cursor-pointer border-bottom"
                                  style={{ cursor: 'pointer' }}
                                  onMouseDown={() => {
                                    setTiendaFilter(code);
                                    setAdminStoreSearch('');
                                    setIsAdminStoreDropdownOpen(false);
                                  }}
                                >
                                  {label}
                                </div>
                              );
                            })}
                            {filteredStores.length === 0 && adminStoreSearch && (
                              <div className="px-3 py-2 text-muted">No se encontraron tiendas</div>
                            )}
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </div>
                )
              }

              <div className="col-md-1 d-flex justify-content-end">
                <button
                  className="ms-auto smu-generic-button solid-btn py-1"
                  onClick={handleFilter}
                  style={{ height: '44px', width: '100px' }}
                >
                  Filtrar
                </button>
              </div>

              {/* Toggle button for date filters */}
              <div className="col-12 mb-3">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
                  onClick={() => setShowDateFilters(!showDateFilters)}
                  style={{
                    color: '#6c757d',
                    fontSize: '14px',
                    border: 'none',
                    background: 'none'
                  }}
                >
                  <span className="me-2">
                    {showDateFilters ? 'v' : '>'}
                  </span>
                  Filtrar fechas
                </button>
              </div>

              {/* Date filters section - conditionally rendered */}
              {showDateFilters && (
                <>
                  {/* Filtro de rango de fechas de creación */}
                  <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Fecha de publicación (desde)</Form.Label>
                  <Form.Control
                    type="date"
                    value={createdAtFrom || ""}
                    onChange={(e) => handleDateChange(setCreatedAtFrom, setCreatedAtFromError, e.target.value)}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: createdAtFromError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {createdAtFromError && (
                    <div className="text-danger small mt-1">{createdAtFromError}</div>
                  )}
                </Form.Group>
                <Form.Group className="mt-1">
                  <Form.Label className="text-muted small mb-1">Fecha de publicación (hasta)</Form.Label>
                  <Form.Control
                    type="date"
                    value={createdAtTo || ""}
                    onChange={(e) => handleDateChange(setCreatedAtTo, setCreatedAtToError, e.target.value)}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: createdAtToError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {createdAtToError && (
                    <div className="text-danger small mt-1">{createdAtToError}</div>
                  )}
                </Form.Group>
              </div>

              {/* Filtro de rango de fechas de inicio */}
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Fecha de inicio (desde)</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDateFrom || ""}
                    onChange={(e) => handleDateChange(setStartDateFrom, setStartDateFromError, e.target.value)}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: startDateFromError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {startDateFromError && (
                    <div className="text-danger small mt-1">{startDateFromError}</div>
                  )}
                </Form.Group>
                <Form.Group className="mt-1">
                  <Form.Label className="text-muted small mb-1">Fecha de inicio (hasta)</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDateTo || ""}
                    onChange={(e) => handleDateChange(setStartDateTo, setStartDateToError, e.target.value)}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: startDateToError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {startDateToError && (
                    <div className="text-danger small mt-1">{startDateToError}</div>
                  )}
                </Form.Group>
              </div>

              {/* Filtro de rango de fechas de fin */}
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Fecha de fin (desde)</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDateFrom || ""}
                    onChange={(e) => handleDateChange(setEndDateFrom, setEndDateFromError, e.target.value)}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: endDateFromError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {endDateFromError && (
                    <div className="text-danger small mt-1">{endDateFromError}</div>
                  )}
                </Form.Group>
                <Form.Group className="mt-1">
                  <Form.Label className="text-muted small mb-1">Fecha de fin (hasta)</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDateTo || ""}
                    onChange={(e) => handleDateChange(setEndDateTo, setEndDateToError, e.target.value)}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: endDateToError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {endDateToError && (
                    <div className="text-danger small mt-1">{endDateToError}</div>
                  )}
                </Form.Group>
              </div>
                </>
              )}

              {/* <div className="col-md-2">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Código Sección</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ingrese código sección"
                    value={codigoSeccion}
                    onChange={(e) => setCodigoSeccion(e.target.value)}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                  />
                </Form.Group>
              </div> */}
              
            </div>
          ) : (
            <div className="d-flex align-items-center ms-5 justify-content-end">
              <SmuSearch
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          )
        }
        
        {/* <div className="d-flex align-items-center ms-5">
          <SmuSearch
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div> */}
      {/* Show current filters summary */}
      {/*
      <div className="mt-2 mb-1">
        <span className="text-muted small">
          <strong>Filtros actuales:</strong>
          {filters.campana && (
            <span className="ms-2">
              <strong>Campaña:</strong> {filters.campana}
            </span>
          )}
          {filters.producto && (
            <span className="ms-2">
              <strong>Producto:</strong> {filters.producto}
            </span>
          )}
          {filters.marca && (
            <span className="ms-2">
              <strong>Marca:</strong> {filters.marca}
            </span>
          )}
          {filters.categoria && (
            <span className="ms-2">
              <strong>Categoría:</strong> {filters.categoria}
            </span>
          )}
          {filters.ean && (
            <span className="ms-2">
              <strong>EAN:</strong> {filters.ean}
            </span>
          )}
          {!filters.campana && !filters.producto && !filters.marca && !filters.categoria && !filters.ean && (
            <span className="ms-2">Sin filtros aplicados</span>
          )}
        </span>
      </div>
      */}
      {/* extended downloading indicator - split based in statusBase*/}
      {isDownloading && (
          <span
            className="text-muted small"
            style={{
              position: "fixed", borderRadius: "8px", padding: "8px 16px",
              bottom: 16, right: 24, zIndex: 9999,
              background: "rgba(255,255,255,0.95)", boxShadow: "0 2px 8px rgba(0,0,0,0.38)"
            }}
          >{loadingPercent <= 40 ? "1/2 Filtrando artículos de faldón..." : "2/2 Generando faldón PDF..."}</span>
        )}
      </Card.Header>
      <Card.Body>
        <SmuTable columns={columns} data={handleFiles} loading={ loading || loadingData } />
      </Card.Body>
    </Card>
  );
};

export default SkirtTable;